/**
 * WRI Aqueduct source-qualified screening. Prefer the accepted cloud 4.0
 * snapshot; retain explicitly qualified historical 2.1 fallback only while
 * that dataset is absent. Neither source establishes local water supply.
 */
import { WRI_AQUEDUCT_SOURCE_LABEL, WRI_AQUEDUCT_SOURCE_URL, WRI_AQUEDUCT_SOURCE_SCOPE } from '../../lib/wriAqueductSource';
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';
import type { WaterStressEvidence } from '../../types/siteEvidence';
import { fetchAqueduct4Screening } from './wriAqueduct4';

export interface WaterStressData extends WaterStressEvidence {
  region: string;             // country name
  subRegion: string;          // state/province name
}

/**
 * Bucketed outcome of a WRI Aqueduct lookup.
 *
 *   - 'ok'           — finite bws_score returned
 *   - 'http-error'   — non-200 from ArcGIS Feature Service
 *   - 'no-features' — HTTP 200 but features[] is empty (out-of-coverage
 *                      or rotated feature-service ID returning a stub)
 *   - 'parse-error' — JSON parse failed
 *   - 'network-error' — fetch threw / aborted
 *   - 'arcgis-error' — ArcGIS returned a {error: {...}} envelope (typical
 *                      when the layer ID is wrong)
 */
type WriOutcomeBucket =
  | 'ok'
  | 'http-error'
  | 'no-features'
  | 'parse-error'
  | 'network-error'
  | 'arcgis-error';

function logWriTelemetry(
  phase: 'request' | 'response',
  payload: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:wri:aqueduct] ${phase}`, payload);
}

/** Map bwsCategory (0-5) to simplified stress level for scoring compatibility. */
function toStressLevel(cat: number): WaterStressData['stressLevel'] {
  if (cat <= 1) return 'Low';       // Low, Low-Medium
  if (cat <= 2) return 'Moderate';  // Medium-High
  return 'High';                    // High, Extremely High, Arid
}

/** Exact legacy mirror: all values retain the 2.1 field mapping.
 * Verified item: de71ba130eda4be195fa7564b0ca947e, owner BucknellGIS.
 * BWS_s is normalized stress; BWS is raw withdrawal-to-supply ratio;
 * BWS_cat is the source category label; BASIN_NAME is a basin, not a state.
 */
const AQUEDUCT_BASE = 'https://services2.arcgis.com/xsh7pVZv42relbEf/arcgis/rest/services/aqueductglobalmaps21shp/FeatureServer';
const AQUEDUCT_LAYER = '0';
export const WRI_AQUEDUCT_CACHE_CONTRACT_VERSION = 'aq4-v1';

/** Retain the existing 2.1 category interpretation until a separately validated migration. */
function parseBwsCategory(attrs: Record<string, number | string | null | undefined>): number {
  const label = String(attrs.BWS_cat ?? attrs.bws_label ?? '');
  const fromLabel = label.match(/^(\d+)/);
  if (fromLabel) return parseInt(fromLabel[1], 10);
  const legacy = attrs.bws_cat as number | undefined;
  if (legacy != null && legacy >= 0) return legacy;
  const scored = attrs.BWS_s as number | undefined;
  if (scored != null && scored >= 0) return Math.min(5, Math.max(0, Math.round(scored)));
  return -1;
}

export const wriAqueductProvider: DataProvider<WaterStressData> = {
  id: 'wri-aqueduct',
  name: 'WRI Aqueduct (edition-qualified regional screening)',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS, // water stress changes slowly
  requiresKey: false,
  cacheKey: ({ lat, lng, geo }) => {
    const parcels = geo?.boundaries?.length ? geo.boundaries : geo?.boundary ? [{ id: 'boundary', polygon: geo.boundary }] : [];
    return `${WRI_AQUEDUCT_CACHE_CONTRACT_VERSION}:${hashCanonicalJson({ lat, lng, parcels: parcels.map(({ id, polygon }) => ({ id, polygon })).sort((a, b) => a.id.localeCompare(b.id)) })}`;
  },

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<WaterStressData>> {
    const current = await fetchAqueduct4Screening(lat, lng, geo);
    if (current) return current;
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`wri-aqueduct` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Aqueduct resolves a basin/sub-basin polygon — a centroid
    // almost always stays in the same basin as the address — so a
    // coordinate-consistency change; near a basin divide it's the parcel
    // that matters, not the mailbox.
    const geometry = JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } });
    const params = new URLSearchParams({
      geometry,
      geometryType: 'esriGeometryPoint',
      spatialRel: 'esriSpatialRelIntersects',
      outFields: 'BWS,BWS_s,BWS_cat,COUNTRY,BASIN_NAME',
      returnGeometry: 'false',
      f: 'json',
    });

    // Layer 0 is the frozen legacy 2.1 mirror described above.
    const url = `${AQUEDUCT_BASE}/${AQUEDUCT_LAYER}/query?${params.toString()}`;
    logWriTelemetry('request', {
      caller: 'wriAqueductProvider.fetch',
      lat,
      lng,
      upstream: `${AQUEDUCT_BASE}/${AQUEDUCT_LAYER}/query`,
    });
    let res: Response;
    try {
      res = await fetchWithTimeout(url, 15_000, geo?.signal);
    } catch (e) {
      logWriTelemetry('response', {
        caller: 'wriAqueductProvider.fetch',
        bucket: 'network-error' as WriOutcomeBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      throw e;
    }
    if (!res.ok) {
      logWriTelemetry('response', {
        caller: 'wriAqueductProvider.fetch',
        bucket: 'http-error' as WriOutcomeBucket,
        httpStatus: res.status,
      });
      throw new Error(`WRI Aqueduct: ${res.status}`);
    }
    let json: { features?: Array<{ attributes: Record<string, unknown> }>; error?: { message?: string; code?: number } } = {};
    try {
      json = await res.json();
    } catch (e) {
      logWriTelemetry('response', {
        caller: 'wriAqueductProvider.fetch',
        bucket: 'parse-error' as WriOutcomeBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      throw e;
    }

    // ArcGIS error envelope — common when the layer ID rotates / is invalid.
    // Returns HTTP 200 with a {error: {...}} body, NOT a non-200. Treat as
    // a misconfiguration signal so PU4 can surface the admin banner.
    if (json.error) {
      logWriTelemetry('response', {
        caller: 'wriAqueductProvider.fetch',
        bucket: 'arcgis-error' as WriOutcomeBucket,
        code: json.error.code ?? null,
        message: json.error.message ?? 'unknown',
      });
      throw new Error(`WRI Aqueduct ArcGIS error: ${json.error.message ?? json.error.code ?? 'unknown'}`);
    }

    const features = json.features;
    if (!features || features.length === 0) {
      logWriTelemetry('response', {
        caller: 'wriAqueductProvider.fetch',
        bucket: 'no-features' as WriOutcomeBucket,
      });
      throw new Error('WRI Aqueduct returned no data for this location');
    }

    const attrs = features[0].attributes as Record<string, number | string | null | undefined>;
    const bwsScore = (attrs.BWS_s as number | undefined) ?? (attrs.bws_score as number | undefined) ?? -1;
    const bwsRaw = (attrs.BWS as number | undefined) ?? (attrs.bws_raw as number | undefined) ?? 0;
    const bwsCat = parseBwsCategory(attrs);
    const bwsLabel = (attrs.BWS_cat as string | undefined) ?? (attrs.bws_label as string | undefined) ?? 'Unknown';
    const overallRisk = bwsScore;
    const region = (attrs.COUNTRY as string | undefined) ?? (attrs.name_0 as string | undefined) ?? '';
    const subRegion = ((attrs.BASIN_NAME as string | undefined) ?? (attrs.name_1 as string | undefined) ?? '').trim();

    logWriTelemetry('response', {
      caller: 'wriAqueductProvider.fetch',
      bucket: 'ok' as WriOutcomeBucket,
      bwsScore,
      bwsCategory: bwsCat,
      region: subRegion || region,
    });

    const data: WaterStressData = {
      sourceEdition: '2.1',
      bwsScore: bwsScore >= 0 ? parseFloat(bwsScore.toFixed(2)) : 0,
      bwsRaw: parseFloat(bwsRaw.toFixed(3)),
      bwsCategory: bwsCat,
      bwsLabel,
      overallWaterRisk: overallRisk >= 0 ? parseFloat(overallRisk.toFixed(2)) : 0,
      region,
      subRegion,
      stressLevel: toStressLevel(bwsCat),
    };

    return {
      data,
      // A pre-activation legacy result must not hide a newly promoted 4.0
      // manifest for the previous provider's 30-day climate-cache lifetime.
      cacheTtlMs: 0,
      sources: [{
        field: 'waterStress',
        source: `${WRI_AQUEDUCT_SOURCE_LABEL} — Water stress: ${bwsLabel} (score ${data.bwsScore}/5). Basin/country: ${subRegion || region}. ${WRI_AQUEDUCT_SOURCE_SCOPE}`,
        url: WRI_AQUEDUCT_SOURCE_URL,
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
